/**
 * CommandCenterDashboard — Admin Command Center Overview.
 *
 * Sections:
 *  1. Risk Radar       — system-level risk indicators
 *  2. Lifecycle Funnel  — farmer onboarding funnel
 *  3. Operational Queue — pending KYC, claims, documents
 *  4. Financial Snapshot— premium, claims paid, credit exposure, escrow
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Alert,
  LinearProgress,
  Skeleton,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  Policy as PolicyIcon,
  Assignment,
  AttachMoney,
  VerifiedUser as KYCIcon,
  Shield as RiskIcon,
  AccountBalance as EscrowIcon,
  Refresh,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, kycAPI, creditAPI } from 'services/api';

// ─── Metric card ──────────────────────────────────────────────
const MetricCard = ({ title, value, subtitle, icon, color = '#3b82f6', trend, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      transition: 'all 0.2s ease',
      '&:hover': onClick
        ? { boxShadow: '0 4px 12px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' }
        : {},
    }}
  >
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, letterSpacing: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            background: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
          }}
        >
          {icon}
        </Box>
      </Box>
      {trend && (
        <Box display="flex" alignItems="center" mt={1.5} gap={0.5}>
          {trend.direction === 'up' ? (
            <TrendingUp sx={{ fontSize: 16, color: trend.positive ? '#22c55e' : '#ef4444' }} />
          ) : (
            <TrendingDown sx={{ fontSize: 16, color: trend.positive ? '#22c55e' : '#ef4444' }} />
          )}
          <Typography variant="caption" sx={{ color: trend.positive ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
            {trend.value}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            {trend.label}
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

// ─── Section header ───────────────────────────────────────────
const SectionHeader = ({ title, subtitle, action }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={4}>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);

// ─── Funnel step ──────────────────────────────────────────────
const FunnelStep = ({ label, count, total, color }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <Box sx={{ flex: 1, textAlign: 'center', px: 0.5 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color }}>
        {count}
      </Typography>
      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          background: '#e2e8f0',
          '& .MuiLinearProgress-bar': { background: color, borderRadius: 3 },
        }}
      />
    </Box>
  );
};

// ─── Queue card ───────────────────────────────────────────────
const QueueCard = ({ title, count, icon, color, onClick, description }) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      border: `1px solid ${color}30`,
      background: `${color}08`,
      boxShadow: 'none',
      transition: 'all 0.2s ease',
      '&:hover': { boxShadow: `0 4px 12px ${color}20`, transform: 'translateY(-2px)' },
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '12px',
          background: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          {description}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color }}>
          {count}
        </Typography>
        <ArrowForward sx={{ fontSize: 16, color: '#94a3b8' }} />
      </Box>
    </CardContent>
  </Card>
);

// ═══════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════
const CommandCenterDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    totalFarmers: 0,
    totalFarms: 0,
    totalPonds: 0,
    totalPolicies: 0,
    activePolicies: 0,
    pendingPolicies: 0,
    totalClaims: 0,
    pendingClaims: 0,
    approvedClaims: 0,
    totalPremium: 0,
    totalClaimsPaid: 0,
    pendingKYC: 0,
    pendingDocuments: 0,
    creditPending: 0,
    // funnel
    registeredFarmers: 0,
    kycVerified: 0,
    farmCreated: 0,
    pondCreated: 0,
    policyCovered: 0,
    claimsProcessed: 0,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashResp, bulkResp, kycResp, creditResp] = await Promise.allSettled([
        dashboardAPI.getAdminDashboard(),
        dashboardAPI.getAdminBulkData(),
        kycAPI.getStats(),
        creditAPI.getApplications(),
      ]);

      const dash = dashResp.status === 'fulfilled' ? dashResp.value.data : {};
      const bulk = bulkResp.status === 'fulfilled' ? bulkResp.value.data : {};
      const kyc = kycResp.status === 'fulfilled' ? kycResp.value.data : {};
      const creditApps = creditResp.status === 'fulfilled' ? creditResp.value.data : [];

      const policies = bulk.policies || [];
      const claims = bulk.claims || [];
      const farms = bulk.farms || [];
      const documents = bulk.documents || [];
      const ponds = farms.flatMap((f) => f.ponds || []);

      setData({
        totalFarmers: dash.total_farmers || 0,
        totalFarms: farms.length,
        totalPonds: ponds.length,
        totalPolicies: policies.length,
        activePolicies: dash.active_policies || 0,
        pendingPolicies: policies.filter((p) => p.status === 'pending').length,
        totalClaims: claims.length,
        pendingClaims: dash.pending_claims || 0,
        approvedClaims: dash.approved_claims || 0,
        totalPremium: dash.total_premium_collected || 0,
        totalClaimsPaid: dash.total_claims_paid || 0,
        pendingKYC: kyc.pending_count || dash.pending_kyc || 0,
        pendingDocuments: documents.filter((d) => d.status === 'pending').length,
        creditPending: Array.isArray(creditApps)
          ? creditApps.filter((a) => a.status === 'pending' || a.status === 'applied').length
          : 0,
        // funnel approximations from available data
        registeredFarmers: dash.total_farmers || 0,
        kycVerified: (dash.total_farmers || 0) - (kyc.pending_count || 0),
        farmCreated: farms.length,
        pondCreated: ponds.length,
        policyCovered: dash.active_policies || 0,
        claimsProcessed: dash.approved_claims || 0,
      });
    } catch (err) {
      setError('Failed to load Command Center data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Box>
        <Box display="flex" gap={2} mb={3}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" width="25%" height={120} />
          ))}
        </Box>
        <Skeleton variant="rounded" height={200} sx={{ mb: 3 }} />
        <Box display="flex" gap={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" width="33%" height={80} />
          ))}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={loadData}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  const lossRatio =
    data.totalPremium > 0
      ? ((data.totalClaimsPaid / data.totalPremium) * 100).toFixed(1)
      : '0.0';

  return (
    <Box>
      {/* ── Header ────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Command Center Overview
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Real-time operational health at a glance
          </Typography>
        </Box>
        <Tooltip title="Refresh all data">
          <IconButton onClick={loadData}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ══════════════════════════════════════════════════════ */}
      {/* 1. RISK RADAR                                        */}
      {/* ══════════════════════════════════════════════════════ */}
      <SectionHeader title="🛡️ Risk Radar" subtitle="System-level risk indicators" />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="ACTIVE POLICIES"
            value={data.activePolicies}
            subtitle={`${data.totalPolicies} total policies`}
            icon={<PolicyIcon />}
            color="#3b82f6"
            onClick={() => navigate('/admin/insurance/policies')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="LOSS RATIO"
            value={`${lossRatio}%`}
            subtitle={data.totalPremium > 0 ? 'Claims / Premium' : 'No premium data'}
            icon={<RiskIcon />}
            color={parseFloat(lossRatio) > 70 ? '#ef4444' : parseFloat(lossRatio) > 40 ? '#f59e0b' : '#22c55e'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="PENDING CLAIMS"
            value={data.pendingClaims}
            subtitle={`${data.totalClaims} total claims`}
            icon={<Assignment />}
            color={data.pendingClaims > 5 ? '#ef4444' : '#f59e0b'}
            onClick={() => navigate('/admin/insurance/claims')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="CREDIT EXPOSURE"
            value={data.creditPending}
            subtitle="Pending applications"
            icon={<EscrowIcon />}
            color="#8b5cf6"
            onClick={() => navigate('/admin/credit/applications')}
          />
        </Grid>
      </Grid>

      {/* ══════════════════════════════════════════════════════ */}
      {/* 2. LIFECYCLE FUNNEL                                   */}
      {/* ══════════════════════════════════════════════════════ */}
      <SectionHeader title="🔄 Lifecycle Funnel" subtitle="Farmer onboarding progression" />
      <Card sx={{ border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" gap={1} flexWrap="wrap">
            <FunnelStep label="Registered" count={data.registeredFarmers} total={data.registeredFarmers} color="#3b82f6" />
            <FunnelStep label="KYC Verified" count={data.kycVerified} total={data.registeredFarmers} color="#06b6d4" />
            <FunnelStep label="Farm Created" count={data.farmCreated} total={data.registeredFarmers} color="#8b5cf6" />
            <FunnelStep label="Pond Created" count={data.pondCreated} total={data.registeredFarmers} color="#f59e0b" />
            <FunnelStep label="Policy Active" count={data.policyCovered} total={data.registeredFarmers} color="#22c55e" />
            <FunnelStep label="Claims OK" count={data.claimsProcessed} total={data.registeredFarmers} color="#10b981" />
          </Box>
        </CardContent>
      </Card>

      {/* ══════════════════════════════════════════════════════ */}
      {/* 3. OPERATIONAL QUEUE                                  */}
      {/* ══════════════════════════════════════════════════════ */}
      <SectionHeader title="📋 Operational Queue" subtitle="Items requiring immediate attention" />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <QueueCard
            title="KYC Pending"
            count={data.pendingKYC}
            icon={<KYCIcon />}
            color="#f59e0b"
            description="Awaiting verification"
            onClick={() => navigate('/admin/operations/kyc')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QueueCard
            title="Claims Pending"
            count={data.pendingClaims}
            icon={<Assignment />}
            color="#ef4444"
            description="Awaiting processing"
            onClick={() => navigate('/admin/insurance/claims')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QueueCard
            title="Documents Pending"
            count={data.pendingDocuments}
            icon={<PolicyIcon />}
            color="#8b5cf6"
            description="Awaiting review"
            onClick={() => navigate('/admin/operations/documents')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QueueCard
            title="Policy Approvals"
            count={data.pendingPolicies}
            icon={<PolicyIcon />}
            color="#06b6d4"
            description="Awaiting approval"
            onClick={() => navigate('/admin/insurance/policies')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QueueCard
            title="Credit Applications"
            count={data.creditPending}
            icon={<AttachMoney />}
            color="#3b82f6"
            description="Awaiting review"
            onClick={() => navigate('/admin/credit/applications')}
          />
        </Grid>
      </Grid>

      {/* ══════════════════════════════════════════════════════ */}
      {/* 4. FINANCIAL SNAPSHOT                                 */}
      {/* ══════════════════════════════════════════════════════ */}
      <SectionHeader title="💰 Financial Snapshot" subtitle="Key financial indicators" />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="PREMIUM COLLECTED"
            value={`₹${(data.totalPremium / 100000).toFixed(2)}L`}
            subtitle="Total premium revenue"
            icon={<AttachMoney />}
            color="#22c55e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="CLAIMS PAID"
            value={`₹${(data.totalClaimsPaid / 100000).toFixed(2)}L`}
            subtitle={`${data.approvedClaims} claims settled`}
            icon={<Assignment />}
            color="#ef4444"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="TOTAL FARMERS"
            value={data.totalFarmers}
            subtitle={`${data.totalFarms} farms, ${data.totalPonds} ponds`}
            icon={<People />}
            color="#8b5cf6"
            onClick={() => navigate('/admin/operations/farmers')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="NET POSITION"
            value={`₹${((data.totalPremium - data.totalClaimsPaid) / 100000).toFixed(2)}L`}
            subtitle={data.totalPremium > data.totalClaimsPaid ? 'Surplus' : 'Deficit'}
            icon={<EscrowIcon />}
            color={data.totalPremium > data.totalClaimsPaid ? '#22c55e' : '#ef4444'}
          />
        </Grid>
      </Grid>

      {/* Spacer */}
      <Box sx={{ pb: 4 }} />
    </Box>
  );
};

export default CommandCenterDashboard;
